import {
  CALENDAR_STATUSES,
  bucketRowsByDay,
  buildMonthGrid,
  distinctPostCount,
  expandPostToRows,
  type CalendarCell,
  type CalendarRow,
} from './publishing/calendarBuckets';
import { clientMediaToPostMedia } from './socialComposerAdapter';
import type { CalendarPost, GbpTopicType, PostPlatform, PostStatus, PostSummaryTarget } from '../types/publishing';

// KyckSocial - the club's content calendar. Two populations, never confused: posts
// made THROUGH Kyck (origin kyck, the Posts page's own list) and posts published
// directly on the platforms (origin external, read-only, fetched live and passed in
// by the caller; this file fetches nothing). NO
// DRAG-TO-RESCHEDULE: a club dragging a scheduled post to the wrong day would
// publish it at the wrong time with nobody watching. Rescheduling happens in the
// composer, where the confirm step lives. The bucketing itself is the staff
// calendar's own pure module (calendarBuckets), reused as-is.

export type ClientCalendarPost = {
  id: string;
  accountId: string;
  status: string;
  content: string;
  media: Array<{ type: string; url: string; thumbnailUrl: string | null; altText: string | null }>;
  targets: Array<{
    platform: PostPlatform;
    fbContentType: string | null;
    igContentType: string | null;
    gbp: { topicType: string } | null;
  }>;
  scheduledFor: string | null;
  publishedAt: string | null;
};

function calendarStatus(s: string): PostStatus {
  // A status the calendar does not know is shown as a draft rather than dropping
  // the post: an invisible post is unfindable.
  return CALENDAR_STATUSES.find((x) => x === s) ?? 'draft';
}

const GBP_TOPICS: readonly GbpTopicType[] = ['STANDARD', 'EVENT', 'OFFER'];
const gbpTopic = (t: string | undefined): GbpTopicType | null => GBP_TOPICS.find((x) => x === t) ?? null;
const placement = (v: string | null): 'feed' | 'reel' | 'story' | null => (v === 'feed' || v === 'reel' || v === 'story' ? v : null);

/** The club's post projection, shaped for the shared calendar bucketing. */
export function toCalendarPost(p: ClientCalendarPost): CalendarPost {
  const targets: PostSummaryTarget[] = p.targets.map((t) => ({
    platform: t.platform,
    fbContentType: placement(t.fbContentType),
    igContentType: placement(t.igContentType),
    gbpTopicType: gbpTopic(t.gbp?.topicType),
  }));
  return {
    id: p.id,
    kyckAccountId: p.accountId,
    // Always 'kyck': this source is the club's own posts list.
    origin: 'kyck',
    status: calendarStatus(p.status),
    content: p.content,
    media: p.media.map(clientMediaToPostMedia),
    platforms: [...new Set(p.targets.map((t) => t.platform))],
    targets,
    scheduledFor: p.scheduledFor,
    publishedAt: p.publishedAt,
  };
}

/** A natively published post, as the server projects it for a club (externalDto.ts). */
export type ClientExternalCalendarPost = {
  id: string;
  content: string;
  media: ClientCalendarPost['media'];
  platforms: string[];
  publishedAt: string | null;
  permalink: string | null;
};

const PLATFORMS: readonly PostPlatform[] = ['facebook', 'instagram', 'googlebusiness'];

/** Shaped for the shared bucketing. origin 'external' is what every mutation path gates on. */
export function toExternalCalendarPost(p: ClientExternalCalendarPost, accountId: string): CalendarPost {
  const platforms = PLATFORMS.filter((x) => p.platforms.includes(x));
  return {
    id: p.id,
    kyckAccountId: accountId,
    origin: 'external',
    status: 'published',
    content: p.content,
    media: p.media.map(clientMediaToPostMedia),
    platforms,
    targets: platforms.map((platform) => ({ platform })),
    scheduledFor: p.publishedAt,
    publishedAt: p.publishedAt,
  };
}

export type ClubCalendarCell =CalendarCell & { rows: CalendarRow[]; postCount: number };

export type ClubMonth = {
  year: number;
  month0: number;
  weeks: ClubCalendarCell[][];
  /** Posts with no day to sit on (a draft with no date). Shown beside the grid, never dropped. */
  undated: CalendarPost[];
  /** Rows that fall in this visible grid, across all of its 42 cells. */
  rowCount: number;
};

export function buildClubMonth(
  posts: readonly ClientCalendarPost[],
  year: number,
  month0: number,
  today: Date,
  external: readonly CalendarPost[] = [],
): ClubMonth {
  const calendarPosts = posts.map(toCalendarPost);
  // Natively published posts join the grid but never the "no date yet" list: that is
  // for the club's own drafts.
  const byDay = bucketRowsByDay([...calendarPosts, ...external]);
  const weeks = buildMonthGrid(year, month0, today).map((week) =>
    week.map((cell) => {
      const rows = byDay.get(cell.key) ?? [];
      return { ...cell, rows, postCount: distinctPostCount(rows) };
    }),
  );
  const undated = calendarPosts.filter((p) => expandPostToRows(p).every((r) => r.scheduledFor === null));
  return { year, month0, weeks, undated, rowCount: weeks.flat().reduce((n, c) => n + c.rows.length, 0) };
}

/** Previous / next month, with the year rolling over. */
export function shiftMonth(year: number, month0: number, delta: number): { year: number; month0: number } {
  const d = new Date(year, month0 + delta, 1);
  return { year: d.getFullYear(), month0: d.getMonth() };
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthLabel = (year: number, month0: number): string => `${MONTHS[month0] ?? ''} ${year}`;

/** One entry per distinct post on a day, earliest first, with the platforms it goes to that day. */
export function postsOnDay(cell: Pick<ClubCalendarCell, 'rows'>): Array<{ post: CalendarPost; time: string; platforms: PostPlatform[] }> {
  const out = new Map<string, { post: CalendarPost; time: string; platforms: PostPlatform[] }>();
  for (const r of cell.rows) {
    const cur = out.get(r.postId);
    if (!cur) out.set(r.postId, { post: r.post, time: r.timeLabel, platforms: r.platform ? [r.platform] : [] });
    else if (r.platform && !cur.platforms.includes(r.platform)) cur.platforms.push(r.platform);
  }
  return [...out.values()];
}

// ---------------------------------------------------------------------------
// Posts page view (List | Calendar) — remembered like the location is, and
// validated on read: a stored value that is not one of the two is ignored.
// ---------------------------------------------------------------------------

export const POSTS_VIEWS = ['list', 'calendar'] as const;
export type PostsView = (typeof POSTS_VIEWS)[number];

const VIEW_KEY = 'kyck-portal-social-posts-view';

export function parsePostsView(raw: string | null | undefined): PostsView {
  return POSTS_VIEWS.find((v) => v === raw) ?? 'list';
}

export function loadPostsView(): PostsView {
  try {
    return parsePostsView(window.sessionStorage.getItem(VIEW_KEY));
  } catch {
    return 'list';
  }
}

export function rememberPostsView(v: PostsView): void {
  try {
    window.sessionStorage.setItem(VIEW_KEY, v);
  } catch {
    /* not remembered; the page still works */
  }
}
